// « Premiers pas » : le parcours proposé à une entreprise qui ouvre Ekera pour la première fois. L'état de chaque
// étape est calculé à partir de ce que l'API rend déjà (pièces déposées, lecture, entretien, carte, rapports) ; rien
// n'est retenu à part, sauf le fait d'avoir refermé le parcours (par carte, dans ce navigateur, sans donnée personnelle).
import type { EtatEntretien, NatureDepot, ResumeRapport, VueDepots } from '../api/types';
import type { Page } from '../routeur';
import { typo } from './typo';

export type EtatEtape = 'fait' | 'en_cours' | 'a_faire' | 'a_consulter' | 'inconnu';

export interface Etape {
  id: 'depot' | 'lecture' | 'entretien' | 'carte' | 'rapport';
  titre: string;
  /** Ce que l'état veut dire, en une phrase (calculé). */
  detail: string;
  etat: EtatEtape;
  page: Page;
  action: string;
}

export const LIBELLE_ETAT_ETAPE: Record<EtatEtape, string> = {
  fait: 'Fait',
  en_cours: 'En cours',
  a_faire: 'À faire',
  a_consulter: 'À consulter',
  inconnu: 'État non lu',
};

export const TON_ETAT_ETAPE: Record<EtatEtape, 'ok' | 'info' | 'attention' | 'neutre'> = {
  fait: 'ok',
  en_cours: 'info',
  a_faire: 'attention',
  a_consulter: 'info',
  inconnu: 'neutre',
};

/** Ce que l'API sait aujourd'hui ; une lecture absente (route refusée ou en erreur) laisse l'étape à « État non lu ». */
export interface DonneesPremiersPas {
  depots?: VueDepots;
  entretien?: EtatEntretien;
  rapports?: ResumeRapport[];
  /** Nombre d'objets de la carte (hors objets retirés). */
  objets?: number;
}

const pluriel = (n: number, un: string, plusieurs: string) => `${n} ${n > 1 ? plusieurs : un}`;

export function calculerEtapes(d: DonneesPremiersPas): Etape[] {
  const pieces = d.depots?.pieces;
  const lecture = d.depots?.lecture ?? null;
  const aLire = pieces?.filter((p) => p.etat === 'recu').length ?? 0;
  const lectureFaite = lecture?.etat === 'terminee' && aLire === 0;

  const depot: Etape = {
    id: 'depot',
    titre: 'Déposer les premières pièces',
    page: 'depot',
    action: 'Déposer des pièces',
    ...(!pieces
      ? { etat: 'inconnu', detail: 'L’état du dépôt n’a pas pu être lu.' }
      : pieces.length === 0
        ? { etat: 'a_faire', detail: 'Aucune pièce déposée. Quelques exports de vos outils suffisent pour commencer.' }
        : { etat: 'fait', detail: `${pluriel(pieces.length, 'pièce déposée', 'pièces déposées')}. Vous pouvez en ajouter à tout moment.` }),
  };

  const etatLecture: Pick<Etape, 'etat' | 'detail'> = !d.depots
    ? { etat: 'inconnu', detail: 'L’état de la lecture n’a pas pu être lu.' }
    : lecture?.etat === 'en_cours'
      ? { etat: 'en_cours', detail: 'La lecture est en cours ; l’écran se met à jour seul.' }
      : lectureFaite
        ? { etat: 'fait', detail: `${pluriel(lecture?.pieces_lues ?? 0, 'pièce lue', 'pièces lues')} ; la carte est à jour.` }
        : lecture?.etat === 'echec' && aLire === 0
          ? { etat: 'a_faire', detail: 'La dernière lecture n’a pas abouti ; la carte n’a pas changé. Vous pouvez la relancer.' }
          : aLire > 0
            ? { etat: 'a_faire', detail: `${pluriel(aLire, 'pièce reçue attend', 'pièces reçues attendent')} d’être lue${aLire > 1 ? 's' : ''}.` }
            : { etat: 'a_faire', detail: 'Déposez d’abord des pièces, puis lancez la lecture.' };
  const lectureEtape: Etape = {
    id: 'lecture',
    titre: 'Lancer la lecture',
    page: 'depot',
    action: 'Lancer la lecture',
    ...etatLecture,
  };

  const e = d.entretien;
  const entretien: Etape = {
    id: 'entretien',
    titre: 'Répondre à l’entretien',
    page: 'entretien',
    action: 'Répondre aux questions',
    ...(!e
      ? { etat: 'inconnu', detail: 'L’état de l’entretien n’a pas pu être lu.' }
      : !e.preparee
        ? { etat: 'a_faire', detail: 'Les questions se préparent une fois la lecture faite : elles ne portent que sur ce que les pièces n’établissent pas.' }
        : e.a_poser === 0
          ? { etat: 'fait', detail: `Plus aucune question n’attend de réponse${e.inconnues ? ` (${pluriel(e.inconnues, 'point à confirmer', 'points à confirmer')})` : ''}.` }
          : { etat: 'en_cours', detail: `${pluriel(e.a_poser, 'question attend', 'questions attendent')} votre réponse.` }),
  };

  const carte: Etape = {
    id: 'carte',
    titre: 'Consulter la carte et les scénarios',
    page: 'carte',
    action: 'Voir la carte',
    ...(d.objets === undefined
      ? { etat: 'inconnu', detail: 'L’état de la carte n’a pas pu être lu.' }
      : d.objets === 0
        ? { etat: 'a_faire', detail: 'La carte est vide pour l’instant : elle se remplit à la première lecture.' }
        : { etat: 'a_consulter', detail: `${pluriel(d.objets, 'élément', 'éléments')} sur la carte, chacun avec la pièce d’où il vient. Parcourez-la, puis les scénarios chiffrés.` }),
  };

  const r = d.rapports;
  const rapport: Etape = {
    id: 'rapport',
    titre: 'Obtenir le rapport de diagnostic',
    page: 'documents',
    action: 'Générer le rapport',
    ...(!r
      ? { etat: 'inconnu', detail: 'L’état des rapports n’a pas pu être lu.' }
      : r.length === 0
        ? { etat: 'a_faire', detail: 'Aucun rapport généré. Le plus utile après la lecture et l’entretien.' }
        : { etat: 'fait', detail: `${pluriel(r.length, 'rapport généré', 'rapports générés')} ; vous pouvez en produire un nouveau quand la carte change.` }),
  };

  return [depot, lectureEtape, entretien, carte, rapport].map((x) => ({ ...x, detail: typo(x.detail), titre: typo(x.titre) }));
}

export function etapesFaites(etapes: readonly Etape[]): number {
  return etapes.filter((e) => e.etat === 'fait').length;
}

/** Les exports utiles pour commencer, avec la nature de dépôt qui leur correspond (lien vers « Dépôt des documents »). */
const EXPORTS: { nature: NatureDepot; libelle: string; aide: string }[] = [
  { nature: 'glpi-json', libelle: 'GLPI', aide: 'l’inventaire des machines et des logiciels (export JSON)' },
  { nature: 'ad-csv', libelle: 'Annuaire', aide: 'les comptes et les ordinateurs de l’annuaire (CSV)' },
  { nature: 'firewall', libelle: 'Pare-feu', aide: 'la configuration, en texte' },
  { nature: 'cloud-json', libelle: 'Cloud', aide: 'la liste des ressources chez votre fournisseur (JSON)' },
  { nature: 'm365-csv', libelle: 'Microsoft 365', aide: 'les utilisateurs et leurs licences (CSV), ou ceux de Google Workspace' },
  { nature: 'achats-csv', libelle: 'Achats', aide: 'les contrats et les achats informatiques (CSV)' },
  { nature: 'tickets-csv', libelle: 'Tickets', aide: 'les demandes et incidents de l’assistance (CSV)' },
  { nature: 'fec-txt', libelle: 'FEC', aide: 'le fichier des écritures comptables, pour chiffrer les pertes à partir de vos comptes' },
];
export const EXPORTS_UTILES: readonly { nature: NatureDepot; libelle: string; aide: string }[] = EXPORTS.map((x) => ({ ...x, aide: typo(x.aide) }));

// ---------------------------------------------------------------- refermer le parcours (par carte, dans ce navigateur)

const PREFIXE = 'ekera.premiers-pas.';
const EVENEMENT = 'ekera:premiers-pas';
const enMemoire = new Set<string>();

function stockage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function premiersPasFermes(cid: string): boolean {
  const s = stockage();
  try {
    if (s?.getItem(PREFIXE + cid) === '1') return true;
  } catch {
    /* stockage refusé : on se rabat sur la mémoire de la page */
  }
  return enMemoire.has(cid);
}

function ecrire(cid: string, ferme: boolean): void {
  if (ferme) enMemoire.add(cid);
  else enMemoire.delete(cid);
  try {
    const s = stockage();
    if (ferme) s?.setItem(PREFIXE + cid, '1');
    else s?.removeItem(PREFIXE + cid);
  } catch {
    /* sans stockage, le choix ne dure que le temps de la page */
  }
  window.dispatchEvent(new Event(EVENEMENT));
}

export const fermerPremiersPas = (cid: string) => ecrire(cid, true);
export const rouvrirPremiersPas = (cid: string) => ecrire(cid, false);

export function abonnerPremiersPas(rappel: () => void): () => void {
  window.addEventListener(EVENEMENT, rappel);
  window.addEventListener('storage', rappel);
  return () => {
    window.removeEventListener(EVENEMENT, rappel);
    window.removeEventListener('storage', rappel);
  };
}
